import { StaticFeatureEnum } from '../../manager/permission/permissions.types';
import { FeatureVO } from '../core/feature.vo';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { Role, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { TranslatableLabelVO } from '../common/translation/translatable-label.vo';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { IVo, entity, field } from '@wizeoo/akm-compute-lib';

/** VO Role */
@entity('wizeoo.allkey.user.message.Role', 'BU')
export class RoleVO implements IVo {

  constructor(model: Model, pSource?: Role) {
    this._source = null;
    if (pSource) {
      this._source = pSource;
    } else {
      this._source = new Role();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
    this._model = model;
  }

  /**
   * Le VO source provenant du back
   */
  private _source: Role;

  protected _model: Model;
  public get model(): Model {
    return this._model;
  }

  public set source(value: Role) {
    this._source = value;
  }
  public get source(): Role {
    return this._source;
  }

  public set uid(uid: string) {
    this._source.uid = uid;
  }
  public get uid(): string {
    return this._source.uid;
  }

  // Id technique
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  public set domain(domain: string) {
    this._source.domain = domain;
  }

  public get domain(): string {
    return this._source.domain;
  }

  @field('string')
  public set code(code: string) {
    this._source.code = code ? code.toUpperCase() : code;
  }
  public get code(): string {
    return this._source.code;
  }

  @field('TranslatedEmb')
  public set label(value: TranslatedEmb) {
    this._source.label = value;
  }
  public get label(): TranslatedEmb {
    return this._source.label;
  }

  @field('boolean')
  public get deprecated(): boolean {
    return this._source.deprecated;
  }
  public set deprecated(value: boolean) {
    this._source.deprecated = value;
  }

  @field('boolean')
  public get deleted(): boolean {
    return this._source.deleted;
  }
  public set deleted(value: boolean) {
    this._source.deleted = value;
  }
  @field('string')
  public get featureCode(): string {
    return this._source.featureCode;
  }
  public set featureCode(value: string) {
    this._source.featureCode = value;
  }

  /**
   * Le périmètre du rôle => c'est à dire le périmètre de la feature associée
   */
  public get perimeter(): string {
    return this.feature ? this.feature.perimeter : null;
  }

  public set businessUnitCode(businessUnitCode: string) {
    this._source.businessUnitCode = businessUnitCode;
  }
  public get businessUnitCode(): string {
    return this._source.businessUnitCode;
  }

  /** Libellé traductible */
  private _labelVO: TranslatableLabelVO;
  public get labelVO(): TranslatableLabelVO {
    if (!this._labelVO) {
      this._labelVO = this.model.localeEngine.newTranslatableVO(this, 'label');
    }
    return this._labelVO;
  }

  @field('boolean')
  public get doRead(): boolean {
    return this._source.doRead;
  }
  public set doRead(value: boolean) {
    this._source.doRead = value;
  }
  @field('boolean')
  public get doCreate(): boolean {
    return this._source.doCreate;
  }
  public set doCreate(value: boolean) {
    this._source.doCreate = value;
  }
  @field('boolean')
  public get doUpdate(): boolean {
    return this._source.doUpdate;
  }
  public set doUpdate(value: boolean) {
    this._source.doUpdate = value;
  }
  @field('boolean')
  public get doDelete(): boolean {
    return this._source.doDelete;
  }
  public set doDelete(value: boolean) {
    this._source.doDelete = value;
  }
  @field('boolean')
  public get doNone(): boolean {
    return this._source.doNone;
  }
  public set doNone(value: boolean) {
    this._source.doNone = value;
  }
  @field('boolean')
  public get doBatch(): boolean {
    return this._source.doBatch;
  }
  public set doBatch(value: boolean) {
    this._source.doBatch = value;
  }

  @field('visionCode')
  public get visionCode(): string {
    return this._source.visionCode;
  }
  public set visionCode(value: string) {
    this._source.visionCode = value;
  }

  @field('string')
  public get tags(): Object {
    return this._source.tags;
  }
  public set tags(value: Object) {
    this._source.tags = value;
  }

  /* ################################################
                    Accesseurs composites
  ################################################ */

  /**
   * Accès au libellé traduit
   */
  public get trsLabel(): string {
    if (!this.isStatic && !!this.label) {
      return this.model.localeEngine.translate(this.label, this.code);
    } else {
      return this.model.localeEngine.translateKey(
        `$.feature.static.${this.featureCode}.$`,
        null,
        this.code
      );
    }
  }

  /**
   * l'objet existe en BDD (id)
   */
  public get exist(): boolean {
    return this.id > 0;
  }

  /**
   * le role n'a pas de feature
   */
  public get withFeature(): boolean {
    return this._source.featureCode !== null && this._source.featureCode !== undefined;
  }

  /**
   * le role n'a pas de feature
   */
  public get withoutFeature(): boolean {
    return this._source.featureCode === null;
  }

  public get withAccess(): boolean {
    return this._source.doNone === false;
  }

  public get withoutAccess(): boolean {
    return this._source.doNone === true;
  }

  /**
   * Indique si le role est lié à une feature dynamique
   */
  public get withDynamicFeature(): boolean {
    const featMap = this.model.featureMap.get(this.model.businessUnitCode);

    if (featMap && featMap.has(this.featureCode)) {
      return true;
    }
  }

  /**
   * Indique si le role est lié à une feature technique
   */
  public get withStaticFeature(): boolean {
    const featMap = this.model.featureMap.get(ViewData.wildcardDomain);

    if (featMap && featMap.has(this.featureCode)) {
      return true;
    }
  }

  /** Rendu Code- label */
  public get codeAndLabelStr(): string {
    const label = this.trsLabel ? this.trsLabel : '';
    return this.code + ' - ' + label;
  }

  /**
   * Retourne le feature vo associé au role
   */
  public get feature(): FeatureVO {
    if (this.withoutFeature) {
      return null;
    }

    // résolution du code de la BU. Si c'est static c'est wildcard sinon code bu du role
    const bu: string = !!StaticFeatureEnum[this.featureCode] ? ViewData.wildcardDomain : this.businessUnitCode;

    if (!this.model.featureMap.has(bu)) {
      return null;
    }

    return this.model.featureMap.get(bu).get(this.featureCode);
  }

  /**
   * Indique si le rôle est statique
   */
  public get isStatic(): boolean {
    return !!this.feature && this.feature.dynamic === false;
  }

}
